import React, { ReactElement, useState } from 'react'
import { Button, Icon } from '@blueprintjs/core'
import {
  ContextMenu2,
  ContextMenu2ChildrenProps,
  Popover2,
} from '@blueprintjs/popover2'
import { StopClickEventPropagation, Box, ErrorState } from 'ui-components'
import { ErrorBoundary } from 'react-error-boundary'
import { TaskT, TaskActionTypeT } from 'ui-io/task-io/task-t'

import TaskRowContextMenu, {
  ContextMenuItem,
} from '../task-context-menu/task-row-context-menu'
import TableRow from '../../table-row'
import {
  TASK_COLUMN_SPECS,
  NormalTaskColumn,
  TaskColumnId,
  UTILITIES_COLUMN,
} from '../task-columns'
import ChildRow from './child-row'
import { TaskCellProps } from '../task-cells/task-cell'
// import ErrorState from '../../error-state'
import DiscussionThread from '../discussion-thread'

const TaskCellData = ({
  column,
  ...otherProps
}: {
  column: NormalTaskColumn
} & TaskCellProps) => {
  const ColumnCell = TASK_COLUMN_SPECS[column].Cell
  return <ColumnCell {...otherProps} />
}

const TaskRow = ({
  task,
  onClick,
  selectedTasks,
  onTasksSelect,
  shownColumnIds,
  contextMenuItems,
  onShowJson,
  highlight,
  dashboardHighlight,
  columnForChildAutoOpening,
  setTaskAction,
  recentlyAdded,
}: {
  task: TaskT
  onClick?: (task: TaskT) => void
  selectedTasks?: TaskT[]
  onTasksSelect?: (task: TaskT[], selected: boolean) => void
  shownColumnIds: TaskColumnId[]
  contextMenuItems?: ContextMenuItem[]
  onShowJson: () => void
  highlight?: boolean
  dashboardHighlight?: boolean
  columnForChildAutoOpening?: TaskColumnId
  setTaskAction: (actionType: TaskActionTypeT) => void
  recentlyAdded?: boolean
}) => {
  /**
   * Each row can optionally have a "child row" that has a cell spanning the
   * whole table with some react element the value of the this element is
   * associated with an "owner" column which is the one which has specified the
   * element to render there
   */
  const [[rowChildOwner, rowChildElement], setRowChildOwnerAndElement] =
    useState<[TaskColumnId | undefined, ReactElement | null]>([undefined, null])
  /**
   * If a row child was explicitly closed, we don't want to open automatically
   * again
   */
  const [rowChildClosed, setRowChildClosed] = useState(false)
  const closeRowChild = () => {
    setRowChildOwnerAndElement(() => [undefined, null])
    setRowChildClosed(true)
  }
  const makeTaskCellProps = (column: NormalTaskColumn): TaskCellProps => {
    const rowChildExists = rowChildElement != null
    const ownsCurrentRowChild = rowChildOwner === column
    const setRowChild = (childToShow: ReactElement | null) => {
      if (childToShow == null) {
        closeRowChild()
      } else {
        setRowChildOwnerAndElement(() => [column, childToShow])
      }
    }
    const shouldSetChild =
      !rowChildOwner && !rowChildClosed && columnForChildAutoOpening === column
    return {
      task,
      onTasksSelect,
      selectedTasks,
      closeRowChild,
      setRowChild,
      rowChildExists,
      ownsCurrentRowChild,
      shouldSetChild,
    }
  }

  const toggleShowChild = () => {
    if (!rowChildElement) {
      setRowChildOwnerAndElement(() => [
        UTILITIES_COLUMN,
        <DiscussionThread key={task._raw.task_id} preloadedTask={task} />,
      ])
    } else {
      closeRowChild()
    }
  }

  const contextMenu = (
    <TaskRowContextMenu
      task={task}
      contextMenuItems={contextMenuItems}
      onShowJson={onShowJson}
      setTaskAction={setTaskAction}
    />
  )

  // This is considered a hack for now. Long term goal is to fix the cascade
  const recentlyAddedStyle = recentlyAdded
    ? { backgroundColor: 'rgba(19, 124, 189, 0.25)' }
    : {}

  return (
    <>
      <ContextMenu2 content={contextMenuItems && contextMenu}>
        {(ctxMenuProps: ContextMenu2ChildrenProps) => (
          <TableRow
            onClick={() => {
              /**
               * Only way I could figure out how to stop the event propagation
               * without messing with the DOM
               */
              if (!ctxMenuProps.contentProps.isOpen && onClick) {
                onClick(task)
              }
            }}
            $highlight={highlight}
            $dashboardHighlight={dashboardHighlight}
            className={ctxMenuProps.className}
            onContextMenu={ctxMenuProps.onContextMenu}
            ref={ctxMenuProps.ref}
          >
            {ctxMenuProps.popover}
            {shownColumnIds.map((column) => (
              <td key={column} style={recentlyAddedStyle}>
                <StopClickEventPropagation>
                  {column === UTILITIES_COLUMN ? (
                    <Box flexDir="row">
                      {contextMenuItems && (
                        <Popover2 content={contextMenu}>
                          <Button minimal>
                            <Icon
                              icon="more"
                              iconSize={20}
                              style={{ transform: 'rotate(90deg)' }}
                            />
                          </Button>
                        </Popover2>
                      )}
                      <Button minimal onClick={() => toggleShowChild()}>
                        <Icon
                          icon={
                            !rowChildElement ? 'chevron-down' : 'chevron-up'
                          }
                          iconSize={20}
                        />
                      </Button>
                    </Box>
                  ) : (
                    <ErrorBoundary fallbackRender={ErrorState}>
                      <TaskCellData
                        column={column}
                        {...makeTaskCellProps(column)}
                      />
                    </ErrorBoundary>
                  )}
                </StopClickEventPropagation>
              </td>
            ))}
          </TableRow>
        )}
      </ContextMenu2>
      <ChildRow
        colSpan={shownColumnIds.length}
        highlight={highlight}
        dashboardHighlight={dashboardHighlight}
        closeRowChild={closeRowChild}
      >
        {rowChildElement}
      </ChildRow>
    </>
  )
}

export default TaskRow
